import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';
 
 
interface Transaction {
    id: number;
    tripCode: string;
    customerName: string;
    amount: number;
    cashInHand: string;
    cashTrnDate: string;
    refNumber?: string;
    cashBankTrnId?: string;
    message?: string;
}
 
interface CashTransactionsState {
    transactions: Transaction[];
    loading: boolean;
    error: string | null;
    selectedTransactions: number[];
    cashBankTrnIds: string[];
    cashAuditedTrnIds: string[];
    transferLoading: boolean;
    transferError: string | null;
    currentFilter: string;
    auditorFilter: string;
    auditorFilterCategory: 'BANK' | 'AUDITED';
}

const initialState: CashTransactionsState = {
    transactions: [],
    loading: false,
    error: null,
    selectedTransactions: [],
    cashAuditedTrnIds: [],
    cashBankTrnIds: [],
    transferLoading: false,
    transferError: null,
    currentFilter: 'cashInHand',
    auditorFilter: '',
    auditorFilterCategory: 'BANK',
};
 
// Async thunks
export const fetchCashTrnsByInhand = createAsyncThunk(
    'cashTransactions/fetchByInhand',
    async (cashInHand: string, { rejectWithValue }) => {
        try {
            const response: any = await api.getCashTrnsByInhand(cashInHand);
            return response.data || [];
        } catch (error: any) {
            console.error('Error fetching cash transactions:', error);
            return rejectWithValue(error.message || 'Failed to fetch cash transactions');
        }
    }
);
 
export const fetchCashBankTrnIds = createAsyncThunk(
    'cashTransactions/fetchBankTrnIds',
    async (cashInHand: string, { rejectWithValue }) => {
        try {
            const response: any = await api.getCashBankTrnIds(cashInHand);
            return response.data || [];
        } catch (error: any) {
            console.error('Error fetching cash bank transaction IDs:', error);
            return rejectWithValue(error.message || 'Failed to fetch transaction IDs');
        }
    }
);
 
export const fetchCashTrnsByTrnId = createAsyncThunk(
    'cashTransactions/fetchByTrnId',
    async (trnId: string, { rejectWithValue }) => {
        try {
            const response: any = await api.getCashTrnsByTrnId(trnId);
            return response.data || [];
        } catch (error: any) {
            console.error('Error fetching cash transaction by ID:', error);
            return rejectWithValue(error.message || 'Failed to fetch transaction');
        }
    }
);
 
export const changeCashInHand = createAsyncThunk(
    'cashTransactions/changeCashInHand',
    async (
        cashInHandData: {
            cashInHand: string;
            cashBankTrnId: string;
            cashTrnDate: string;
            transactionIds: number[];
        },
        { rejectWithValue }
    ) => {
        try {
            const response = await api.changeCashInHand(cashInHandData);
            return response;
        } catch (error: any) {
            console.error('Error changing cash in hand:', error);
            return rejectWithValue(error.message || 'Failed to transfer cash');
        }
    }
);

export const fetchCashAuditedTrnIds = createAsyncThunk(
    'cashTransactions/fetchAuditedTrnIds',
    async (cashInHand: string, { rejectWithValue }) => {
        try {
            const response: any = await api.getCashBankTrnIds(cashInHand);
            return response.data || [];
        } catch (error: any) {
            console.error('Error fetching cash audited transaction IDs:', error);
            return rejectWithValue(error.message || 'Failed to fetch audited transaction IDs');
        }
    }
);
 
const cashTransactionsSlice = createSlice({
    name: 'cashTransactions',
    initialState,
    reducers: {
        setSelectedTransactions: (state, action: PayloadAction<number[]>) => {
            state.selectedTransactions = action.payload;
        },
        toggleTransactionSelection: (state, action: PayloadAction<number>) => {
            const id = action.payload;
            const index = state.selectedTransactions.indexOf(id);
            if (index > -1) {
                state.selectedTransactions.splice(index, 1);
            } else {
                state.selectedTransactions.push(id);
            }
        },
        selectAllTransactions: (state) => {
            state.selectedTransactions = state.transactions.map(t => t.id);
        },
        clearSelectedTransactions: (state) => {
            state.selectedTransactions = [];
        },
        clearError: (state) => {
            state.error = null;
        },
        clearTransferError: (state) => {
            state.transferError = null;
        },
        setCurrentFilter: (state, action: PayloadAction<string>) => {
            state.currentFilter = action.payload;
        },
        setAuditorFilter: (state, action: PayloadAction<string>) => {
            state.auditorFilter = action.payload;
        },
        setAuditorFilterWithCategory: (state, action: PayloadAction<{ trnId: string; category: 'BANK' | 'AUDITED' }>) => {
            state.auditorFilter = action.payload.trnId;
            state.auditorFilterCategory = action.payload.category;
        }
    },
    extraReducers: (builder) => {
        // Fetch cash transactions by inhand
        builder
            .addCase(fetchCashTrnsByInhand.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCashTrnsByInhand.fulfilled, (state, action) => {
                state.loading = false;
                state.transactions = action.payload;
                // state.selectedTransactions = []; // Clear selections on new data
            })
            .addCase(fetchCashTrnsByInhand.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
 
        // Fetch cash bank transaction IDs
        builder
            .addCase(fetchCashBankTrnIds.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCashBankTrnIds.fulfilled, (state, action) => {
                state.loading = false;
                state.cashBankTrnIds = action.payload;
            })
            .addCase(fetchCashBankTrnIds.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
            
            // Fetch cash audited transaction IDs
        builder
            .addCase(fetchCashAuditedTrnIds.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCashAuditedTrnIds.fulfilled, (state, action) => {
                state.loading = false;
                state.cashAuditedTrnIds = action.payload;
            })
            .addCase(fetchCashAuditedTrnIds.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
        // Fetch cash transaction by ID
        builder
            .addCase(fetchCashTrnsByTrnId.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCashTrnsByTrnId.fulfilled, (state, action) => {
                state.loading = false;
                // Filter transactions based on the current category
                const allTransactions = action.payload;
                state.transactions = allTransactions.filter((trn: Transaction) => 
                    trn.cashInHand === state.auditorFilterCategory
                );
            })
            .addCase(fetchCashTrnsByTrnId.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
 
        // Change cash in hand (transfer)
        builder
            .addCase(changeCashInHand.pending, (state) => {
                state.transferLoading = true;
                state.transferError = null;
            })
            .addCase(changeCashInHand.fulfilled, (state) => {
                state.transferLoading = false;
                state.selectedTransactions = []; // Clear selections after successful transfer
            })
            .addCase(changeCashInHand.rejected, (state, action) => {
                state.transferLoading = false;
                state.transferError = action.payload as string;
            });
    },
});
 
export const {
    setSelectedTransactions,
    toggleTransactionSelection,
    selectAllTransactions,
    clearSelectedTransactions,
    clearError,
    clearTransferError,
    setCurrentFilter,
    setAuditorFilter,
    setAuditorFilterWithCategory
} = cashTransactionsSlice.actions;
 
export default cashTransactionsSlice.reducer;